import Icon1 from "@assets/icons/Icon";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { useDataStore } from "@store/user_org";
import { useState, useRef } from "react";
import { isOwnerOrAdmin } from "@utils/permissions";
import { showNotification } from "context/notification.service";
import { organizationsApi } from "@services/api/organizations";


export const Company = () => {
  const orgData = useDataStore((state) => state.orgData);
  const userData = useDataStore((state) => state.userData);
  const updateOrgData = useDataStore((state) => state.updateOrgData);
  const fetchOrgData = useDataStore((state) => state.fetchOrgData);

  // Local state for org form
  const [orgName, setOrgName] = useState(orgData?.infos.name || "");
  const [orgEmail, setOrgEmail] = useState(orgData?.infos.email || "");
  const [orgPhoneNumber, setOrgPhoneNumber] = useState(
    orgData?.infos.phone_number || "",
  );
  const [orgSiret, setOrgSiret] = useState(orgData?.infos.siret || "");
  const [orgCity, setOrgCity] = useState(orgData?.infos.city || "");
  const [orgPostal, setOrgPostal] = useState(orgData?.infos.postal || "");
  const [orgAddress, setOrgAddress] = useState(orgData?.infos.address || "");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoFileInputRef = useRef<HTMLInputElement>(null);

  const handleLogoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file || !orgData) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      showNotification({
        type: "error",
        message: "Format invalide. Acceptés : JPG, PNG, WEBP",
      });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showNotification({ type: "error", message: "La taille dépasse 5MB" });
      return;
    }

    setIsUploadingLogo(true);
    try {
      const { compressImage } = await import("@utils/compressImage");
      const compressed = await compressImage(file);
      await organizationsApi.uploadLogo(orgData.id, compressed);
      await fetchOrgData(orgData.id);
      showNotification({
        type: "success",
        message: "Logo enregistré avec succès",
      });
    } catch {
      showNotification({
        type: "error",
        message: "Échec du téléchargement du logo",
      });
    } finally {
      setIsUploadingLogo(false);
      if (logoFileInputRef.current) logoFileInputRef.current.value = "";
    }
  };

  const handleSaveOrg = async () => {
    if (!orgData?.id) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      await updateOrgData(orgData.id, {
        infos: {
          name: orgName,
          email: orgEmail,
          phone_number: orgPhoneNumber,
          siret: orgSiret,
          city: orgCity,
          postal: orgPostal,
          address: orgAddress,
        },
      });
      showNotification({
        type: "success",
        message: "Modifications enregistrées avec succès",
      });
    } catch (error: any) {
      console.error("Failed to update org:", error);
      const errorMessage = "Échec de l'enregistrement des modifications";
      setSaveError(errorMessage);
      showNotification({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-fit pb-96 mt-10 mx-4 lg:ml-10 w-full max-w-700">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <Text1>Entreprise</Text1>
          <Text2>Configurez votre entreprise ici.</Text2>
          <Text3>
            Gérez vos informations d'entreprise, vos préférences et les
            paramètres associés.
          </Text3>
        </div>
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h4 text-neutral-400">Logo de l'entreprise</div>
          <img
            src={orgData?.infos.logo_url || require("@assets/photos/heric.png")}
            className="w-24 h-24 rounded-xl border object-contain flex-shrink-0"
          ></img>
          <input
            ref={logoFileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleLogoUpload}
            className="hidden"
          />
          <SecondaryButton
            startIcon={<Icon1 name="join" size={18}></Icon1>}
            label={
              isUploadingLogo
                ? "Téléchargement..."
                : orgData?.infos.logo_url
                  ? "Changer le logo"
                  : "Ajouter une image"
            }
            variant="large"
            disabled={isUploadingLogo || !isOwnerOrAdmin(userData)}
            onClick={() => logoFileInputRef.current?.click()}
          ></SecondaryButton>
        </div>
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <TextField
            label="ID"
            value={orgData?.id || ""}
            placeholder=""
            disabled={true}
          />{" "}
          <TextField
            label="Pseudo/Prénom (court)"
            value={orgName}
            onChange={(value) => setOrgName(value)}
            placeholder="Dupont"
            disabled={!isOwnerOrAdmin(userData)}
          />
          <TextField
            label="Adresse e-mail de contact"
            placeholder="contact@life.com"
            value={orgEmail}
            onChange={(value) => setOrgEmail(value)}
            disabled={!isOwnerOrAdmin(userData)}
          />
          <TextField
            label="Numéro de téléphone de contact"
            placeholder="06 12 34 56 78"
            type="phone"
            value={orgPhoneNumber}
            onChange={(value) => setOrgPhoneNumber(value)}
            disabled={!isOwnerOrAdmin(userData)}
          />
          <TextField
            label="SIRET"
            placeholder="123 456 789 00010"
            value={orgSiret}
            onChange={(value) => setOrgSiret(value)}
            disabled={!isOwnerOrAdmin(userData)}
          />
          <TextField
            label="Ville"
            placeholder="Rennes"
            value={orgCity}
            onChange={(value) => setOrgCity(value)}
            disabled={!isOwnerOrAdmin(userData)}
          />
          <TextField
            label="Code postal"
            placeholder="35000"
            value={orgPostal}
            onChange={(value) => setOrgPostal(value)}
            disabled={!isOwnerOrAdmin(userData)}
          />
          <TextField
            label="Adresse"
            placeholder="1 rue des fleurs"
            value={orgAddress}
            onChange={(value) => setOrgAddress(value)}
            disabled={!isOwnerOrAdmin(userData)}
          />
          <TextField
            label="Adresse e-mail d'envoi"
            placeholder="noreply@entreprise.com"
            value={orgData?.infos.sender_email || ""}
            disabled={true}
          />
        </div>
        <div className="flex flex-col gap-2">
          {saveError && (
            <div className="text-h4 text-red-500 mt-2">{saveError}</div>
          )}
          <Button
            className="mt-4"
            label={
              isSaving ? "Enregistrement..." : "Enregistrer les modifications"
            }
            onClick={handleSaveOrg}
            disabled={isSaving || !isOwnerOrAdmin(userData)}
          ></Button>
        </div>
      </div>
    </div>
  );
};
